import React from 'react';
import { CaretLeft, CaretRight } from '@core/icons-react';
import { Tag, seg } from '../../../tags/Tag';
import { Scrollbar } from '../../../scrollbar/Scrollbar';

interface TableFooterProps {
  currentPage: number;
  totalPages: number;
  totalResults: number;
  resultsPerPage: number;
  startIndex: number;
  endIndex: number;
  onPageChange: (page: number) => void;
  onResultsPerPageChange: (perPage: number) => void;
  scrollElement?: HTMLDivElement | null;
}

const resultsOptions = [5, 10, 25, 50, 100];

const TableFooterComponent: React.FC<TableFooterProps> = ({
  currentPage,
  totalPages,
  totalResults,
  resultsPerPage,
  startIndex,
  endIndex,
  onPageChange,
  onResultsPerPageChange,
  scrollElement
}) => {
  return (
    <div className="primary-table-pagination-section">
      {/* Left: Direction Controls */}
      <div className="primary-table-footer-direction-controls">
        <Tag
          template="action-sm"
          segments={[seg.icon(CaretLeft, { size: 16 })]}
          disabled={currentPage === 1}
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          title="Previous page"
        />
        <Tag
          template="action-sm"
          segments={[seg.icon(CaretRight, { size: 16 })]}
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          title="Next page"
        />
      </div>
      
      {/* Middle: Horizontal Scrollbar - Always render, let component handle visibility */}
      <div className="primary-table-footer-scrollbar">
        {scrollElement && (
          <Scrollbar 
            scrollElement={scrollElement}
          />
        )}
      </div>
      
      {/* Right: Results Controls */}
      <div className="primary-table-footer-results-controls">
        <span className="primary-table-results-count">
          {startIndex + 1}-{endIndex} of {totalResults} Results
        </span>
        <Tag
          template="action-sm"
          segments={[seg.text(`${resultsPerPage} Results`, { size: 'md' })]}
          dropdown="option"
          dropdownOptions={resultsOptions.map(n => ({
            id: String(n),
            value: String(n),
            label: `${n} Results`
          }))}
          selectedItem={String(resultsPerPage)}
          onSelectionChange={(value) => onResultsPerPageChange(parseInt(value))}
          dropdownPosition="right"
          dropdownDirection="up"
          dropdownRequireSelection
          dropdownDefaultValue="5"
          dropdownVariant="default"
        />
        </div>
      </div>
  );
};

export const TableFooter = React.memo(TableFooterComponent, (prevProps, nextProps) => {
  // Only re-render if data values change, ignore function reference changes
  return (
    prevProps.currentPage === nextProps.currentPage &&
    prevProps.totalPages === nextProps.totalPages &&
    prevProps.totalResults === nextProps.totalResults &&
    prevProps.resultsPerPage === nextProps.resultsPerPage &&
    prevProps.startIndex === nextProps.startIndex &&
    prevProps.endIndex === nextProps.endIndex &&
    prevProps.scrollElement === nextProps.scrollElement
  );
});

